import { openAuthModal } from "@blocks/_shared/auth/use-auth-modal";
import apiFetch from "@wordpress/api-fetch";
import { dispatch } from "@wordpress/data";
import { createRoot } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
/**
 * Spend Rules List Block - Frontend View
 *
 * Renders the spend rules list on the frontend with dynamic data.
 * Only hydrates for enrolled users who can actually redeem rewards.
 */
import type React from "react";
import { type FC, useEffect, useState } from "react";
import { joinProgram } from "~/shared/queries";
import Button from "../_shared/button";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemLabel,
	ItemMedia,
	Modal,
} from "../_shared/components";
import { useEnrollment } from "../_shared/hooks/use-enrollment";
import { SpendRulesGridSkeleton } from "../_shared/skeleton/patterns";
import {
	type ClaimedReward,
	type Contact,
	ENROLLMENT_STORE_NAME,
	type Tier,
} from "../_shared/store/enrollment-store";
import { replaceStrings } from "../_shared/utils/replacers";
import { getTranslatedText } from "../_shared/utils/translated-text";

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	showRulesToNotEnrolled: boolean;
	cardLabelLevel: number;
	buttonTextSignIn: string;
	buttonTextJoin: string;
	buttonTextRedeem: string;
	messageInsufficientCredits: string;
	messageRedeemSuccess: string;
	messageGuest: string;
	messageNotEnrolled: string;
	emptyStateMessage: string;
	emptyStateButtonTextGuest: string;
	emptyStateButtonTextNotEnrolled: string;
	progressMeterText: string;
	imageHeight: number;
	imageFit: "contain" | "cover" | "fill";
	imageAspectRatio: "1:1" | "4:3" | "3:4";
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
	buttonTextLocationOnly: string;
	showProductPhoto: boolean;
}

const aspectRatioToCss = (ratio: Attributes["imageAspectRatio"]): string =>
	ratio.replace(":", " / ");

interface SpendRule {
	id: number;
	label: { value: string | Record<string, string> };
	creditCost: { value: number };
	svg: string;
	image?: { value: string };
	description?: { value: string | Record<string, string> };
	rewardTerms?: { value: string | Record<string, string> | null };
	discount?: { value: number }; // For discount-based rewards
	offlineOnly?: { value: string };
	productImage?: { value: string | null };
}

/**
 * Choose which image URL to render for a rule.
 *
 * Manual reward image always wins; product photo is a fallback when the
 * block is configured to fall back AND the rule has a resolved product photo
 * (only present on FREE_PRODUCT rules with at least one selected product).
 */
const resolveRuleImageUrl = (
	rule: SpendRule,
	showProductPhoto: boolean,
): string | null => {
	if (rule.image?.value) {
		return rule.image.value;
	}
	if (showProductPhoto && rule.productImage?.value) {
		return rule.productImage.value;
	}
	return null;
};

interface SpendRulesListViewProps {
	attributes: Attributes;
	initialContent?: string;
}

// Pull button color overrides from the block's Styles panel and turn them
// into inline styles. PanelColorSettings already returns resolved CSS values
// (hex for custom, var(--wp--preset--color--slug) for presets) so we apply
// them directly — no extra wrapping.
const getButtonInlineStyles = (attributes: Attributes): React.CSSProperties => {
	const styles: React.CSSProperties = {};
	if (attributes.buttonTextColor) {
		styles.color = attributes.buttonTextColor;
	}
	if (attributes.buttonBackgroundColor) {
		styles.backgroundColor = attributes.buttonBackgroundColor;
	}
	return styles;
};

// ============================================================================
// State Components
// ============================================================================

const GuestState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	return (
		<div className="leat-spend-rules-list__empty-state">
			<p>{attributes.messageGuest}</p>
			<Button
				onClick={() => openAuthModal({ intent: "auto" })}
				variant="contained"
			>
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const NotEnrolledEmptyState: FC<{ attributes: Attributes }> = ({
	attributes,
}) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			const userId = window.leat?.userId ?? null;
			if (!userId) {
				throw new Error("User ID not found");
			}

			await joinProgram(userId);
			window.location.reload();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to join program";
			setJoinError(message);
			setIsJoining(false);
		}
	};

	return (
		<div className="leat-spend-rules-list__empty-state">
			<p>{attributes.messageNotEnrolled}</p>
			<Button
				onClick={handleJoinProgram}
				disabled={isJoining}
				variant="contained"
			>
				{isJoining ? "Joining..." : attributes.emptyStateButtonTextNotEnrolled}
			</Button>
			{joinError && <p className="leat-spend-rules-list__error">{joinError}</p>}
		</div>
	);
};

const GuestWithRulesState: FC<{
	attributes: Attributes;
	spendRules: SpendRule[];
	creditsCurrency: string;
}> = ({ attributes, spendRules, creditsCurrency }) => {
	const buttonInlineStyles = getButtonInlineStyles(attributes);

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: "repeat(3, 1fr)",
	};

	return (
		<div className="leat-spend-rules-list__grid" style={gridStyle}>
			{spendRules.map((rule) => (
				<GuestSpendRuleCard
					key={rule.id}
					rule={rule}
					creditsCurrency={creditsCurrency}
					labelLevel={attributes.cardLabelLevel}
					buttonTextSignIn={attributes.buttonTextSignIn}
					buttonInlineStyles={buttonInlineStyles}
					imageAspectRatio={attributes.imageAspectRatio}
					showProductPhoto={attributes.showProductPhoto}
				/>
			))}
		</div>
	);
};

const LoggedInNotEnrolledState: FC<{
	attributes: Attributes;
	spendRules: SpendRule[];
	creditsCurrency: string;
}> = ({ attributes, spendRules, creditsCurrency }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			// Get current user ID from window.leat
			const userId = window.leat?.userId ?? null;

			if (!userId) {
				throw new Error("User ID not found");
			}

			await joinProgram(userId);

			// Reload the page to refresh enrollment status
			window.location.reload();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to join program";
			setJoinError(message);
			setIsJoining(false);
		}
	};

	const buttonInlineStyles = getButtonInlineStyles(attributes);

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: "repeat(3, 1fr)",
	};

	return (
		<>
			<div className="leat-spend-rules-list__grid" style={gridStyle}>
				{spendRules.map((rule) => (
					<NotEnrolledSpendRuleCard
						key={rule.id}
						rule={rule}
						creditsCurrency={creditsCurrency}
						labelLevel={attributes.cardLabelLevel}
						buttonTextJoin={attributes.buttonTextJoin}
						buttonInlineStyles={buttonInlineStyles}
						imageAspectRatio={attributes.imageAspectRatio}
						showProductPhoto={attributes.showProductPhoto}
						onJoinProgram={handleJoinProgram}
						isJoiningProgram={isJoining}
						joinProgramError={joinError}
					/>
				))}
			</div>
		</>
	);
};

// ============================================================================
// Component
// ============================================================================

const SpendRulesListView: FC<SpendRulesListViewProps> = ({
	attributes,
	initialContent,
}) => {
	const {
		status,
		credits,
		creditsCurrency,
		isLoading: enrollmentLoading,
	} = useEnrollment();
	const [spendRules, setSpendRules] = useState<SpendRule[]>([]);
	const [isLoadingRules, setIsLoadingRules] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hasReceivedData, setHasReceivedData] = useState(false);

	// Function to fetch spend rules
	const fetchSpendRules = async () => {
		// Don't fetch if still determining enrollment status
		if (enrollmentLoading) {
			return;
		}

		// For guests, only fetch when the block is configured to show rules to non-enrolled users.
		if (status === "guest" && !attributes.showRulesToNotEnrolled) {
			setIsLoadingRules(false);
			setHasReceivedData(true);
			return;
		}

		// Don't fetch for non-enrolled if not showing rules to them
		if (
			status === "loggedInNotEnrolled" &&
			!attributes.showRulesToNotEnrolled
		) {
			setIsLoadingRules(false);
			setHasReceivedData(true);
			return;
		}

		try {
			// Fetch spend rules for enrolled users and logged-in-not-enrolled (if showing rules)
			const rules = await apiFetch<SpendRule[]>({
				path: "/leat/v1/spend-rules",
			});

			// Order by credit cost (highest first)
			const orderedRules = rules.sort(
				(a, b) => (b.creditCost.value ?? 0) - (a.creditCost.value ?? 0),
			);
			setSpendRules(orderedRules);
			setIsLoadingRules(false);
			setHasReceivedData(true);
		} catch (err) {
			// Check if this is a "user not enrolled" error (404 or specific error codes)
			// The error can be a Response object (with .status) or an error object (with .code/.data)
			const isNotEnrolledError =
				err.status === 404 || // Response object
				err.code === "spend-rules-claim" ||
				err.code === "no_contact_uuid" ||
				err.data?.status === 404 ||
				(err.message && err.message.toLowerCase().includes("not found")) ||
				(err.message && err.message.toLowerCase().includes("user not found"));

			if (isNotEnrolledError) {
				console.log(
					"[Spend Rules] User not enrolled (404) - showing empty state",
				);
				setSpendRules([]);
				setIsLoadingRules(false);
				setHasReceivedData(true);
			} else {
				// Real error - show error message
				console.error("[Spend Rules] API Error:", err);
				setError(err.message || "Failed to load rewards");
				setIsLoadingRules(false);
			}
		}
	};

	// Fetch spend rules when needed
	useEffect(() => {
		fetchSpendRules();
	}, [status, enrollmentLoading, attributes.showRulesToNotEnrolled]);

	// Show initial server-rendered content while loading
	if (!hasReceivedData && initialContent) {
		return <div dangerouslySetInnerHTML={{ __html: initialContent }} />;
	}

	// Show loading state
	if (isLoadingRules || enrollmentLoading) {
		return (
			<SpendRulesGridSkeleton
				columns={3}
				ariaMessage={__("Loading rewards...", "leat-crm")}
			/>
		);
	}

	// Show error state
	if (error) {
		return (
			<div className="leat-spend-rules-list__error">
				<p>{error}</p>
			</div>
		);
	}

	// Show empty state ONLY for enrolled users with no rewards
	// Guest and non-enrolled states are handled below
	if (spendRules.length === 0 && hasReceivedData && status === "enrolled") {
		return (
			<div className="leat-spend-rules-list__empty">
				<p>{attributes.emptyStateMessage}</p>
			</div>
		);
	}

	// ============================================================================
	// Handle Enrollment States
	// ============================================================================

	// Guest State: Show grid with sign-in CTAs if configured, otherwise a sign-in prompt
	if (status === "guest") {
		if (attributes.showRulesToNotEnrolled && spendRules.length > 0) {
			return (
				<GuestWithRulesState
					attributes={attributes}
					spendRules={spendRules}
					creditsCurrency={creditsCurrency ?? "points"}
				/>
			);
		}
		return <GuestState attributes={attributes} />;
	}

	// Logged In Not Enrolled State: Show join program prompt
	if (status === "loggedInNotEnrolled") {
		// If we want to show rules to not enrolled AND we have rules, show the grid
		if (attributes.showRulesToNotEnrolled && spendRules.length > 0) {
			return (
				<LoggedInNotEnrolledState
					attributes={attributes}
					spendRules={spendRules}
					creditsCurrency={creditsCurrency ?? "points"}
				/>
			);
		}
		// Otherwise show the empty state with join button
		return <NotEnrolledEmptyState attributes={attributes} />;
	}

	// ============================================================================
	// Enrolled State: Show Rewards List
	// ============================================================================

	const buttonInlineStyles = getButtonInlineStyles(attributes);

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: "repeat(3, 1fr)",
	};

	return (
		<div className="leat-spend-rules-list__grid" style={gridStyle}>
			{spendRules.map((rule) => (
				<SpendRuleCard
					key={rule.id}
					rule={rule}
					userCredits={credits ?? 0}
					creditsCurrency={creditsCurrency ?? "points"}
					labelLevel={attributes.cardLabelLevel}
					buttonTextRedeem={attributes.buttonTextRedeem}
					buttonInlineStyles={buttonInlineStyles}
					messageInsufficientCredits={attributes.messageInsufficientCredits}
					messageRedeemSuccess={attributes.messageRedeemSuccess}
					progressMeterText={attributes.progressMeterText}
					imageAspectRatio={attributes.imageAspectRatio}
					buttonTextLocationOnly={attributes.buttonTextLocationOnly}
					showProductPhoto={attributes.showProductPhoto}
					onRedeemSuccess={fetchSpendRules}
				/>
			))}
		</div>
	);
};

// ============================================================================
// Progress Meter Component
// ============================================================================

const ProgressMeter: FC<{
	currentBalance: number;
	requiredCredits: number;
	creditsCurrency: string;
	progressMeterText: string;
}> = ({
	currentBalance,
	requiredCredits,
	creditsCurrency,
	progressMeterText,
}) => {
	const remaining = Math.max(requiredCredits - currentBalance, 0);
	const progressPercentage = Math.min(
		Math.round((currentBalance / requiredCredits) * 100),
		100,
	);

	const displayText = replaceStrings(progressMeterText, [
		{
			"{{current_balance}}": currentBalance.toString(),
			"{{credits_currency}}": creditsCurrency,
			"{{required_credits}}": requiredCredits.toString(),
			"{{remaining_credits}}": remaining.toString(),
		},
	]);

	return (
		<div className="leat-spend-rule-card__progress">
			<div className="leat-spend-rule-card__progress-meta">
				<span className="leat-spend-rule-card__progress-label">
					{displayText}
				</span>
				<span className="leat-spend-rule-card__progress-percentage">
					{progressPercentage}%
				</span>
			</div>
			<div className="leat-spend-rule-card__progress-bar">
				<div
					className="leat-spend-rule-card__progress-fill"
					style={{ width: `${progressPercentage}%` }}
				/>
			</div>
		</div>
	);
};

// ============================================================================
// Card Component
// ============================================================================

const SpendRuleCard: FC<{
	rule: SpendRule;
	userCredits: number;
	creditsCurrency: string;
	labelLevel: number;
	buttonTextRedeem: string;
	buttonInlineStyles?: React.CSSProperties;
	messageInsufficientCredits: string;
	messageRedeemSuccess: string;
	progressMeterText: string;
	imageAspectRatio: Attributes["imageAspectRatio"];
	buttonTextLocationOnly: string;
	showProductPhoto?: boolean;
	onRedeemSuccess?: () => void;
}> = ({
	rule,
	userCredits,
	creditsCurrency,
	labelLevel,
	buttonTextRedeem,
	buttonInlineStyles,
	messageInsufficientCredits,
	messageRedeemSuccess,
	progressMeterText,
	imageAspectRatio,
	buttonTextLocationOnly,
	showProductPhoto = false,
	onRedeemSuccess,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isRedeeming, setIsRedeeming] = useState(false);
	const [redeemedCoupon, setRedeemedCoupon] = useState<string | null>(null);
	const [redeemError, setRedeemError] = useState<string | null>(null);
	const [isCopied, setIsCopied] = useState(false);

	const copyRedeemedCoupon = async () => {
		if (!redeemedCoupon) return;
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(redeemedCoupon);
			} else {
				const textarea = document.createElement("textarea");
				textarea.value = redeemedCoupon;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			}
		} catch (err) {
			console.error("Failed to copy coupon code:", err);
		}
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	// Get translated label and description text
	const labelValue = rule.label.value;
	const label =
		typeof labelValue === "string" ? labelValue : getTranslatedText(labelValue);
	const creditCost = rule.creditCost.value;

	// Get translated description if it exists
	const descriptionValue = rule.description?.value;
	const description = descriptionValue
		? typeof descriptionValue === "string"
			? descriptionValue
			: getTranslatedText(descriptionValue)
		: null;

	const termsValue = rule.rewardTerms?.value;
	const rewardTerms = termsValue
		? typeof termsValue === "string"
			? termsValue
			: getTranslatedText(termsValue)
		: null;

	// Prepare replacement variables
	const replacementVariables = {
		"{{credits}}": creditCost?.toString() || "0",
		"{{credits_currency}}": creditsCurrency,
		"{{discount}}": rule.discount?.value?.toString() || "0",
	};

	// Replace variables in label
	const displayLabel = replaceStrings(label, [replacementVariables]);

	// Replace variables in description
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;

	const displayRewardTerms = rewardTerms
		? replaceStrings(rewardTerms, [replacementVariables])
		: null;

	// Check if user can afford this reward
	const canAfford = userCredits >= creditCost;

	const photoUrl = resolveRuleImageUrl(rule, showProductPhoto);

	// Create dynamic heading tag
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	const openModal = (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		if (!canAfford) return;
		setRedeemedCoupon(null);
		setRedeemError(null);
		setIsCopied(false);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const handleRedeem = async () => {
		if (!canAfford || isRedeeming) return;

		setIsRedeeming(true);
		setRedeemError(null);
		try {
			const response = await apiFetch<{
				success: boolean;
				coupon: string;
				reward_reception: any;
			}>({
				path: `/leat/v1/spend-rules/${rule.id}/redeem`,
				method: "POST",
			});

			if (response.coupon) {
				setRedeemedCoupon(response.coupon);
			}

			const contactResponse = await apiFetch<{
				contact: Contact;
				tier: Tier | null;
				claimedRewards: ClaimedReward[];
			}>({
				path: "/leat/v1/contact",
			});

			const storeDispatch = dispatch(ENROLLMENT_STORE_NAME) as any;
			storeDispatch.setContact(contactResponse.contact);
			storeDispatch.setTier(contactResponse.tier);
			storeDispatch.setClaimedRewards(contactResponse.claimedRewards);

			if (onRedeemSuccess) {
				await onRedeemSuccess();
			}
		} catch (err) {
			console.error("[Spend Rules] Failed to redeem reward:", err);

			let errorMessage: string = __(
				"Failed to redeem reward. Please try again.",
				"leat-crm",
			);
			if (err && typeof err === "object") {
				if (
					"message" in err &&
					typeof (err as { message?: unknown }).message === "string"
				) {
					errorMessage = (err as { message: string }).message;
				} else if (
					"code" in err &&
					typeof (err as { code?: unknown }).code === "string"
				) {
					errorMessage = (err as { code: string }).code;
				}
			}
			setRedeemError(errorMessage);
		} finally {
			setIsRedeeming(false);
		}
	};

	const ctaText = replaceStrings(buttonTextRedeem, [
		{ ...replacementVariables, "{{label}}": displayLabel },
	]);

	return (
		<>
			<Item
				variant={!canAfford ? "disabled" : "default"}
				style={
					{
						"--leat-image-aspect-ratio": aspectRatioToCss(imageAspectRatio),
					} as React.CSSProperties
				}
			>
				{photoUrl && (
					<ItemMedia>
						<img src={photoUrl} alt={__("Reward image", "leat-crm")} />
					</ItemMedia>
				)}

				<ItemContent>
					<ItemLabel as={LabelTag}>{displayLabel}</ItemLabel>

					{creditCost !== null && (
						<p className="leat-spend-rule-card__cost">
							{creditCost} {creditsCurrency}
						</p>
					)}

					{displayDescription && (
						<ItemDescription>{displayDescription}</ItemDescription>
					)}
				</ItemContent>

				<ItemActions>
					{rule.offlineOnly?.value === "on" ? (
						<Button
							renderAs="span"
							variant="contained"
							className="leat-spend-rule-card__redeem-button leat-button--location"
						>
							{buttonTextLocationOnly}
						</Button>
					) : canAfford ? (
						<Button
							type="button"
							variant="contained"
							className="leat-spend-rule-card__redeem-button leat-button--pill"
							onClick={openModal}
							style={buttonInlineStyles}
						>
							{buttonTextRedeem}
						</Button>
					) : creditCost !== null && creditCost > 0 ? (
						<ProgressMeter
							currentBalance={userCredits}
							requiredCredits={creditCost}
							creditsCurrency={creditsCurrency}
							progressMeterText={progressMeterText}
						/>
					) : null}
				</ItemActions>
			</Item>

			<Modal
				isOpen={isModalOpen}
				onClose={closeModal}
				title={__("Redeem reward", "leat-crm")}
				className={`leat-redeem-modal${redeemedCoupon ? " leat-redeem-modal--redeemed" : ""}`}
			>
				{redeemedCoupon ? (
					<>
						<div className="leat-redeem-modal__reward-row">
							<div className="leat-redeem-modal__reward-media">
								{photoUrl ? (
									<img src={photoUrl} alt="" />
								) : rule.svg ? (
									// eslint-disable-next-line react/no-danger
									<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
								) : null}
							</div>
							<p className="leat-redeem-modal__reward-title">{displayLabel}</p>
						</div>

						<div className="leat-redeem-modal__code">
							<div className="leat-redeem-modal__code-info">
								<span className="leat-redeem-modal__code-label">
									{__("Discount code", "leat-crm")}
								</span>
								<code className="leat-redeem-modal__code-value">
									{redeemedCoupon}
								</code>
							</div>
							<Button
								type="button"
								variant="contained"
								className="leat-redeem-modal__copy-button"
								onClick={copyRedeemedCoupon}
								style={buttonInlineStyles}
								aria-label={
									isCopied ? __("Copied!", "leat-crm") : __("Copy", "leat-crm")
								}
							>
								{isCopied ? __("Copied!", "leat-crm") : __("Copy", "leat-crm")}
							</Button>
						</div>
					</>
				) : (
					<>
						<div className="leat-redeem-modal__preview">
							<div className="leat-redeem-modal__preview-media">
								{photoUrl ? (
									<img src={photoUrl} alt="" />
								) : rule.svg ? (
									// eslint-disable-next-line react/no-danger
									<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
								) : null}
							</div>
							<div className="leat-redeem-modal__preview-info">
								<p className="leat-redeem-modal__preview-title">
									{displayLabel}
								</p>
								{creditCost !== null && (
									<p className="leat-redeem-modal__preview-cost">
										{creditCost} {creditsCurrency}
									</p>
								)}
							</div>
						</div>

						{displayRewardTerms && (
							<div className="leat-redeem-modal__terms">
								<p className="leat-redeem-modal__terms-label">
									{__("Reward terms", "leat-crm")}
								</p>
								<p className="leat-redeem-modal__terms-body">
									{displayRewardTerms}
								</p>
							</div>
						)}

						{redeemError && (
							<p className="leat-redeem-modal__error">{redeemError}</p>
						)}
						<Button
							type="button"
							variant="contained"
							className="leat-redeem-modal__cta leat-button--pill"
							onClick={handleRedeem}
							disabled={isRedeeming}
							style={buttonInlineStyles}
						>
							{isRedeeming ? __("Redeeming...", "leat-crm") : ctaText}
						</Button>
					</>
				)}
			</Modal>
		</>
	);
};

// ============================================================================
// Guest Spend Rule Card
// ============================================================================

const GuestSpendRuleCard: FC<{
	rule: SpendRule;
	creditsCurrency: string;
	labelLevel: number;
	buttonTextSignIn: string;
	buttonInlineStyles?: React.CSSProperties;
	imageAspectRatio: Attributes["imageAspectRatio"];
	showProductPhoto?: boolean;
}> = ({
	rule,
	creditsCurrency,
	labelLevel,
	buttonTextSignIn,
	buttonInlineStyles,
	imageAspectRatio,
	showProductPhoto = false,
}) => {
	const labelValue = rule.label.value;
	const label =
		typeof labelValue === "string" ? labelValue : getTranslatedText(labelValue);
	const creditCost = rule.creditCost.value;

	const descriptionValue = rule.description?.value;
	const description = descriptionValue
		? typeof descriptionValue === "string"
			? descriptionValue
			: getTranslatedText(descriptionValue)
		: null;

	const replacementVariables = {
		"{{credits}}": creditCost?.toString() || "0",
		"{{credits_currency}}": creditsCurrency,
		"{{discount}}": rule.discount?.value?.toString() || "0",
	};

	const displayLabel = replaceStrings(label, [replacementVariables]);
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;

	const photoUrl = resolveRuleImageUrl(rule, showProductPhoto);

	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	return (
		<Item
			variant="default"
			style={
				{
					"--leat-image-aspect-ratio": aspectRatioToCss(imageAspectRatio),
				} as React.CSSProperties
			}
		>
			<ItemMedia>
				{photoUrl ? (
					<img src={photoUrl} alt={label} />
				) : (
					// biome-ignore lint/security/noDangerouslySetInnerHtml: This is controlled SVG content from backend
					<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
				)}
			</ItemMedia>

			<ItemContent>
				<ItemLabel as={LabelTag}>{displayLabel}</ItemLabel>
				{creditCost !== null && (
					<p className="leat-spend-rule-card__cost">
						{creditCost} {creditsCurrency}
					</p>
				)}
				{displayDescription && (
					<ItemDescription>{displayDescription}</ItemDescription>
				)}
			</ItemContent>

			<ItemActions>
				<Button
					onClick={() => openAuthModal({ intent: "auto" })}
					variant="contained"
					className="leat-spend-rule-card__redeem-button leat-button--pill"
					style={buttonInlineStyles}
				>
					{buttonTextSignIn}
				</Button>
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Not Enrolled Spend Rule Card
// ============================================================================

const NotEnrolledSpendRuleCard: FC<{
	rule: SpendRule;
	creditsCurrency: string;
	labelLevel: number;
	buttonTextJoin: string;
	buttonInlineStyles?: React.CSSProperties;
	imageAspectRatio: Attributes["imageAspectRatio"];
	showProductPhoto?: boolean;
	onJoinProgram?: () => void;
	isJoiningProgram?: boolean;
	joinProgramError?: string | null;
}> = ({
	rule,
	creditsCurrency,
	labelLevel,
	buttonTextJoin,
	buttonInlineStyles,
	imageAspectRatio,
	showProductPhoto = false,
	onJoinProgram,
	isJoiningProgram = false,
	joinProgramError,
}) => {
	const labelValue = rule.label.value;
	const label =
		typeof labelValue === "string" ? labelValue : getTranslatedText(labelValue);
	const creditCost = rule.creditCost.value;

	const descriptionValue = rule.description?.value;
	const description = descriptionValue
		? typeof descriptionValue === "string"
			? descriptionValue
			: getTranslatedText(descriptionValue)
		: null;

	const replacementVariables = {
		"{{credits}}": creditCost?.toString() || "0",
		"{{credits_currency}}": creditsCurrency,
		"{{discount}}": rule.discount?.value?.toString() || "0",
	};

	const displayLabel = replaceStrings(label, [replacementVariables]);
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;

	const photoUrl = resolveRuleImageUrl(rule, showProductPhoto);

	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	return (
		<Item
			variant="default"
			style={
				{
					"--leat-image-aspect-ratio": aspectRatioToCss(imageAspectRatio),
				} as React.CSSProperties
			}
		>
			<ItemMedia>
				{photoUrl ? (
					<img src={photoUrl} alt={label} />
				) : (
					// biome-ignore lint/security/noDangerouslySetInnerHtml: This is controlled SVG content from backend
					<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
				)}
			</ItemMedia>

			<ItemContent>
				<ItemLabel as={LabelTag}>{displayLabel}</ItemLabel>
				{creditCost !== null && (
					<p className="leat-spend-rule-card__cost">
						{creditCost} {creditsCurrency}
					</p>
				)}
				{displayDescription && (
					<ItemDescription>{displayDescription}</ItemDescription>
				)}
			</ItemContent>

			<ItemActions>
				<Button
					onClick={onJoinProgram}
					disabled={isJoiningProgram}
					variant="contained"
					className="leat-spend-rule-card__redeem-button leat-button--pill"
					style={buttonInlineStyles}
				>
					{isJoiningProgram ? __("Joining...", "leat-crm") : buttonTextJoin}
				</Button>
				{joinProgramError && (
					<div className="leat-item__error">{joinProgramError}</div>
				)}
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize spend rules list blocks
 * Hydrates for all enrollment states: guest, loggedInNotEnrolled, and enrolled
 */
export function initSpendRulesListBlocks(): void {
	// Wait for WordPress data stores to be ready
	if (
		typeof window.wp === "undefined" ||
		typeof window.wp.data === "undefined"
	) {
		setTimeout(initSpendRulesListBlocks, 100);
		return;
	}

	// Find all spend rules list blocks (all enrollment states)
	const blocks = document.querySelectorAll<HTMLElement>(
		'[data-block-type="spend-rules-list"]',
	);

	if (blocks.length === 0) {
		return;
	}

	// Hydrate each block
	for (const block of blocks) {
		const attributes: Attributes = {
			columns: Number.parseInt(block.dataset.columns || "3", 10),
			showRulesToNotEnrolled: block.dataset.showRulesToNotEnrolled === "true",
			cardLabelLevel: Number.parseInt(block.dataset.cardLabelLevel || "4", 10),
			buttonTextSignIn: block.dataset.buttonTextSignIn || "Sign In",
			buttonTextJoin: block.dataset.buttonTextJoin || "Join Program",
			buttonTextRedeem: block.dataset.buttonTextRedeem || "Redeem",
			messageInsufficientCredits:
				block.dataset.messageInsufficientCredits || "Insufficient credits",
			messageRedeemSuccess:
				block.dataset.messageRedeemSuccess || "Coupon code: {{coupon}}",
			messageGuest:
				block.dataset.messageGuest || "Please sign in to see available rewards",
			messageNotEnrolled:
				block.dataset.messageNotEnrolled ||
				"Join our program to unlock exclusive rewards",
			emptyStateMessage:
				block.dataset.emptyStateMessage || "No rewards available at this time",
			emptyStateButtonTextGuest:
				block.dataset.emptyStateButtonTextGuest || "Sign In",
			emptyStateButtonTextNotEnrolled:
				block.dataset.emptyStateButtonTextNotEnrolled || "Join Program",
			progressMeterText:
				block.dataset.progressMeterText ||
				"Earn {{remaining_credits}} more {{credits_currency}} to unlock",
			imageHeight: Number.parseInt(block.dataset.imageHeight || "200", 10),
			imageFit:
				(block.dataset.imageFit as "contain" | "cover" | "fill") || "cover",
			imageAspectRatio:
				(block.dataset.imageAspectRatio as Attributes["imageAspectRatio"]) ||
				"1:1",
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
			buttonTextLocationOnly:
				block.dataset.buttonTextLocationOnly || "Claimable on location only",
			showProductPhoto: block.dataset.showProductPhoto === "true",
		};

		// Preserve initial server-rendered content
		const initialContent = block.innerHTML;

		// Create React root and render
		const root = createRoot(block);
		root.render(
			<SpendRulesListView
				attributes={attributes}
				initialContent={initialContent}
			/>,
		);
	}
}

// Auto-initialize on DOM ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initSpendRulesListBlocks);
} else {
	initSpendRulesListBlocks();
}
